import * as React from "react";
import { Redirect } from "react-router-dom";
import { loader } from "graphql.macro";
import { useQuery } from "../../hooks/useQuery";
import { TableFragmentFragment } from "../../graphql/TableFragment.generated";
import { useTranslation } from "react-i18next";
import {
  TableQueryQuery,
  TableQueryQueryVariables,
} from "./graphql/TableQuery.generated";
import GameDefinitions from "../Games";
import { AbsoluteCenter } from "../AbsoluteCenter";
import { Loader } from "../Loader";
import { ErrorBoundary, FallbackProps } from "../ErrorBoundary";
import { LinkWithSound } from "../AudioProvider";

const TABLE_QUERY = loader("./graphql/TableQuery.graphql");

const FallbackComponent = ({
  error,
  sectionId,
}: { sectionId: string } & FallbackProps) => {
  const { t } = useTranslation();
  return (
    <AbsoluteCenter>
      <div className="w-1/3 p-4 bg-blue-800 border-2 border-blue-900 rounded shadow-xl">
        <span className="font-bold text-white">{t("game_error")}</span>
        {error !== undefined && (
          <div className="p-2 mt-2 text-white bg-red-700 rounded">
            {error.message}
          </div>
        )}
        <LinkWithSound
          className="w-full mt-2 rounded btn btn-gray-500 btn-sm"
          to={`/area/${sectionId}`}
        >
          {t("back")}
        </LinkWithSound>
      </div>
    </AbsoluteCenter>
  );
};

const Game = ({ section, ...rest }: TableFragmentFragment) => {
  const Component = GameDefinitions[section.type].GameComponent;
  const { t } = useTranslation();
  return (
    <ErrorBoundary
      fallback={(props) => (
        <FallbackComponent sectionId={section.id} {...props} />
      )}
    >
      <React.Suspense
        fallback={
          <AbsoluteCenter>
            <Loader description={t("loading_game")} />
          </AbsoluteCenter>
        }
      >
        <Component sectionId={section.id} game={section.type} {...rest} />
      </React.Suspense>
    </ErrorBoundary>
  );
};

export const Table = ({
  sectionId,
  tableId,
}: {
  sectionId: string;
  tableId: string;
}) => {
  const [{ data }] = useQuery<TableQueryQuery, TableQueryQueryVariables>({
    query: TABLE_QUERY,
    variables: { tableId },
  });

  const { t } = useTranslation();
  const table = data === undefined ? undefined : data?.table ?? null;
  if (table === null) return <Redirect to={`/area/${sectionId}`} />;
  else if (table === undefined)
    return (
      <AbsoluteCenter>
        <Loader description={t("loading_game")} />
      </AbsoluteCenter>
    );
  else return <Game {...table} />;
};
